/**
 * OnboardingStrip
 * ---------------
 * Ambient, one-line system signal rendered below the header in vendor mode.
 * Reads phase from OnboardingContext; renders only for phases that require
 * user action (profile_draft / ready_to_publish / payouts_pending). Hides
 * itself entirely for live/role_granted/not_started phases so it never adds
 * noise.
 *
 * Premium, subtle, not a banner. ~32px tall, single sentence + small CTA.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useOnboarding } from '../context/OnboardingContext';
import { usePrimaryFocus } from '../context/PrimaryFocusContext';
import { useAuth } from '../App';

const PHASE_TINT = {
  profile_draft:    'bg-amber-50/70 text-amber-900 border-amber-200/70',
  ready_to_publish: 'bg-emerald-50/70 text-emerald-900 border-emerald-200/70',
  payouts_pending:  'bg-sky-50/70 text-sky-900 border-sky-200/70',
};

const OnboardingStrip = () => {
  const { phase, strip, adminBlocked, ctaAction, hasMeaningfulProfile } = useOnboarding();
  const { activeRole, user } = useAuth();
  const { hasPrimaryFocus } = usePrimaryFocus();
  const navigate = useNavigate();

  // Visibility gating: vendor mode only + dual-role aware.
  if (!user || user.is_admin) return null;
  if (activeRole !== 'vendor') return null;

  // Primary Focus consolidation: when the current surface owns a large
  // Primary Focus card (e.g., the Vendor Dashboard "welcome" tab), suppress
  // this ambient strip to avoid duplicating the same signal in two places.
  // Generic by design — any future surface can declare itself via
  // <DeclarePrimaryFocus /> and benefit from the same suppression.
  if (hasPrimaryFocus) return null;

  // Phase 4: isolated admin-blocked signal. Fires ONLY when the vendor asked
  // to be live and admin hid them. Does NOT reuse onboarding phase messaging.
  if (adminBlocked) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="admin-blocked-strip"
        className="w-full border-b bg-amber-50 text-amber-900 border-amber-300/70"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-8 text-[12px] font-medium">
            <span className="truncate">
              Your profile is currently paused. Contact support for details.
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!phase || !strip) return null;

  const tint = PHASE_TINT[phase] || 'bg-slate-50/70 text-slate-900 border-slate-200/70';

  // Phase 4.7: legacy-aware copy swap. Vendors in role_granted / not_started
  // with a meaningful profile already built see sharper "activate" wording,
  // matching the guide card tone. Same flow, same CTA destination.
  const isLegacyUnsigned =
    hasMeaningfulProfile && (phase === 'role_granted' || phase === 'not_started');
  const stripLabel = isLegacyUnsigned
    ? 'One last step to activate your profile'
    : strip.label;
  const stripCta = isLegacyUnsigned
    ? 'Accept Agreement'
    : strip.cta;

  const handleClick = () => {
    // Phase 4.5: respect the canonical phase-to-action mapping. For phases
    // whose real first blocker is the participation agreement (not_started /
    // role_granted), append the `onboarding_action=agreement` flag so the
    // dashboard host opens the agreement dialog directly. Without the flag,
    // a plain tab switch happens.
    const openAgreement = ctaAction && ctaAction.type === 'agreement';
    const qs = openAgreement
      ? `tab=${strip.tab}&onboarding_action=agreement`
      : `tab=${strip.tab}`;
    navigate(`/vendor/dashboard?${qs}`);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="onboarding-strip"
      data-phase={phase}
      className={`w-full border-b ${tint} transition-colors duration-200`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={handleClick}
          className="w-full flex items-center justify-between gap-3 h-8 text-[12px] font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white focus:ring-current/40 rounded-sm"
          data-testid="onboarding-strip-cta"
        >
          <span className="truncate">{stripLabel}</span>
          <span className="inline-flex items-center gap-1 shrink-0 opacity-90 hover:opacity-100 transition-opacity">
            {stripCta}
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </span>
        </button>
      </div>
    </div>
  );
};

export default OnboardingStrip;
